import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ComplianceValidationPanel = ({ minimumWage, payrollRuns }) => {
  const [validationResults, setValidationResults] = useState(null);
  const [validating, setValidating] = useState(false);

  const runComplianceValidation = () => {
    setValidating(true);

    setTimeout(() => {
      const results = {
        minimumWageCompliance: {
          status: 'pass',
          message: `All hourly rates meet or exceed minimum wage of PGK ${minimumWage?.hourlyRate || '4.00'}/hr`,
          violations: 0,
          checked: 230
        },
        overtimeCompliance: {
          status: 'pass',
          message: 'All overtime calculations use correct 1.5x multiplier',
          violations: 0,
          checked: 45
        },
        nasfundCompliance: {
          status: 'warning',
          message: '2 employees missing NASFUND registration',
          violations: 2,
          checked: 230
        },
        swtCompliance: {
          status: 'pass',
          message: 'All SWT calculations follow PNG progressive tax rates',
          violations: 0,
          checked: 230
        },
        workingHoursCompliance: {
          status: 'warning',
          message: '3 employees exceeded maximum 60 hours/week',
          violations: 3,
          checked: 230
        },
        leaveEntitlementCompliance: {
          status: 'pass',
          message: 'All employees have correct leave balances',
          violations: 0,
          checked: 230
        }
      };

      setValidationResults(results);
      setValidating(false);
    }, 2000);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pass': return 'CheckCircle';
      case 'warning': return 'AlertTriangle';
      case 'fail': return 'XCircle';
      default: return 'Info';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pass': return 'text-green-600 bg-green-50 border-green-200';
      case 'warning': return 'text-amber-600 bg-amber-50 border-amber-200';
      case 'fail': return 'text-red-600 bg-red-50 border-red-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Compliance Validation Engine</h3>
          <p className="text-sm text-gray-600 mt-1">Automated checks against PNG Employment Act requirements</p>
        </div>
        <Button
          onClick={runComplianceValidation}
          disabled={validating}
          className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2.5 rounded-lg font-medium flex items-center gap-2"
        >
          {validating ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Validating...
            </>
          ) : (
            <>
              <Icon name="Shield" className="h-5 w-5" />
              Run Validation
            </>
          )}
        </Button>
      </div>

      {/* Minimum Wage Info */}
      {minimumWage && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Info" className="h-5 w-5 text-blue-600" />
            <h4 className="font-semibold text-blue-900">Current PNG Minimum Wage</h4>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-blue-700">Hourly Rate: <span className="font-bold">PGK {minimumWage?.hourlyRate?.toFixed(2)}</span></p>
            </div>
            <div>
              <p className="text-blue-700">Effective Date: <span className="font-bold">{new Date(minimumWage?.effectiveDate)?.toLocaleDateString()}</span></p>
            </div>
          </div>
          <p className="text-xs text-blue-600 mt-2">Reference: {minimumWage?.determinationReference}</p>
        </div>
      )}

      {/* Validation Results */}
      {validationResults ? (
        <div className="space-y-3">
          <h4 className="font-semibold text-gray-900">Validation Results</h4>
          
          {Object.entries(validationResults)?.map(([key, result]) => (
            <div key={key} className={`border-2 rounded-lg p-4 ${getStatusColor(result?.status)}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1">
                  <Icon name={getStatusIcon(result?.status)} className="h-6 w-6 mt-0.5" />
                  <div>
                    <h5 className="font-semibold text-gray-900 mb-1">
                      {key?.replace(/([A-Z])/g, ' $1')?.replace(/^./, str => str?.toUpperCase())}
                    </h5>
                    <p className="text-sm text-gray-700">{result?.message}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-600">
                      <span>Checked: {result?.checked}</span>
                      <span>Violations: {result?.violations}</span>
                    </div>
                  </div>
                </div>
                {result?.violations > 0 && (
                  <Button className="text-sm font-medium flex items-center gap-1">
                    <Icon name="FileText" className="h-4 w-4" />
                    View Details
                  </Button>
                )}
              </div>
            </div>
          ))}

          {/* Summary */}
          <div className="bg-gradient-to-br from-emerald-50 to-green-50 border-2 border-emerald-200 rounded-lg p-5">
            <div className="flex items-center gap-2 mb-3">
              <Icon name="CheckCircle" className="h-6 w-6 text-emerald-600" />
              <h4 className="font-semibold text-gray-900">Compliance Summary</h4>
            </div>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-green-600">
                  {Object.values(validationResults)?.filter(r => r?.status === 'pass')?.length}
                </p>
                <p className="text-sm text-gray-600">Passed</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-600">
                  {Object.values(validationResults)?.filter(r => r?.status === 'warning')?.length}
                </p>
                <p className="text-sm text-gray-600">Warnings</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-red-600">
                  {Object.values(validationResults)?.filter(r => r?.status === 'fail')?.length}
                </p>
                <p className="text-sm text-gray-600">Failed</p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">
          <Icon name="Shield" className="h-16 w-16 mx-auto mb-4 text-gray-400" />
          <p className="text-lg font-medium">No validation results yet</p>
          <p className="text-sm mt-1">Click "Run Validation" to check payroll compliance</p>
        </div>
      )}
    </div>
  );
};

export default ComplianceValidationPanel;